import { computed, Injectable, signal } from '@angular/core';

/**
 * Site/NVR current time for EventQ event headers.
 * Accepts an optional IANA timezone so the clock shows site-local time
 * instead of the operator's local time (matches Resolve).
 */
@Injectable({ providedIn: 'root' })
export class SiteCurrentTimeService {
  private readonly nowMs = signal(Date.now());
  private readonly timezone = signal<string | null>(null);
  private readonly timerId: ReturnType<typeof setInterval>;

  readonly formattedTime = computed(() => this.format(this.nowMs(), this.timezone()));
  /** Site IANA zone applied by event detail load; null uses the operator clock. */
  readonly siteTimezone = computed(() => this.timezone());

  /**
   * Minutes elapsed since local midnight IN THE SITE TIMEZONE.
   * Falls back to the browser timezone when no site timezone is set.
   */
  readonly minutesFromMidnight = computed(() => {
    const tz = this.timezone();
    const nowMs = this.nowMs();
    if (tz) {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
        }).formatToParts(new Date(nowMs));
        const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
        const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
        return hour * 60 + minute;
      } catch {
        // fall through to browser-local calculation
      }
    }
    const date = new Date(nowMs);
    return date.getHours() * 60 + date.getMinutes();
  });

  constructor() {
    this.timerId = setInterval(() => this.nowMs.set(Date.now()), 1000);
  }

  /**
   * Set the site's IANA timezone (e.g. 'America/Chicago').
   * Pass null to revert to the operator's local clock.
   */
  setTimezone(iana: string | null): void {
    this.timezone.set(iana);
  }

  /** Test / teardown helper. */
  stop(): void {
    clearInterval(this.timerId);
  }

  private format(ms: number, tz: string | null): string {
    if (tz) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZoneName: 'short',
        });
        const parts = formatter.formatToParts(new Date(ms));
        const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
        const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
        const second = parts.find((p) => p.type === 'second')?.value ?? '00';
        const tzName =
          (parts.find((p) => p.type === 'timeZoneName')?.value ?? 'UTC').replace(
            /[^A-Za-z]/g,
            '',
          ) || 'UTC';
        return `${hour}:${minute}:${second} ${tzName}`;
      } catch {
        // fall through to browser-local format
      }
    }

    const date = new Date(ms);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const tzRaw =
      new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value ?? 'UTC';
    const tzAbbr = tzRaw.replace(/[^A-Za-z]/g, '') || 'UTC';
    return `${hours}:${minutes}:${seconds} ${tzAbbr}`;
  }
}
